import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SCHEMA_ID = 'schema-org-jsonld';

/**
 * Inyecta JSON-LD Schema.org en el <head> del documento.
 * Google lo lee para mostrar rich snippets (estrellas, precios, FAQs).
 */
const SchemaOrg = ({ schemas = [] }) => {
  const location = useLocation();

  useEffect(() => {
    // Remove previous schema scripts
    document.querySelectorAll(`script[data-schema="${SCHEMA_ID}"]`).forEach(el => el.remove());

    // Inject new schemas
    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', SCHEMA_ID);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll(`script[data-schema="${SCHEMA_ID}"]`).forEach(el => el.remove());
    };
  }, [schemas, location.pathname]);

  return null;
};

/** Organization schema - used on homepage */
export const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "MisTramites.mx",
  "url": "https://mistramites.mx",
  "logo": "https://mistramites.mx/logo-optimized.png",
  "description": "Plataforma líder en gestión de trámites gubernamentales en México. Más de 10,000 trámites procesados con 95% de éxito.",
  "foundingDate": "2015",
  "areaServed": {
    "@type": "Country",
    "name": "México"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+52-55-8535-3729",
    "contactType": "customer service",
    "availableLanguage": "Spanish",
    "areaServed": "MX"
  },
  "sameAs": [
    "https://wa.me/525585353729"
  ]
});

/** WebSite schema with search - used on homepage */
export const buildWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "MisTramites.mx",
  "url": "https://mistramites.mx",
  "description": "Gestiona tus trámites gubernamentales en México de forma rápida, segura y profesional.",
  "publisher": {
    "@type": "Organization",
    "name": "MisTramites.mx"
  }
});

/**
 * Breadcrumb schema - for category pages
 * Google shows: Inicio > Categorías > Vehiculares
 */
export const buildBreadcrumbSchema = (categoryName, categorySlug) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Inicio",
      "item": "https://mistramites.mx/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Categorías",
      "item": "https://mistramites.mx/categorias"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": categoryName,
      "item": `https://mistramites.mx/categoria/${categorySlug}`
    }
  ]
});

/**
 * Service schema with offer catalog - for category detail pages.
 * Shows each tramite as a service with price in Google.
 * 
 * Dynamically generated from API data:
 * - category.name, category.description
 * - tramites[].name, description, price, time, documents
 */
export const buildCategoryServiceSchema = (category, tramites = []) => {
  if (!category) return null;

  const offers = tramites.map(t => {
    const offer = {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": t.name,
        "description": t.description || `Trámite de ${t.name} en México`
      },
      "priceCurrency": "MXN",
      "availability": "https://schema.org/InStock"
    };

    if (t.price_type === 'fijo' && t.fixed_price) {
      offer.price = String(t.fixed_price);
    } else {
      offer.price = "0";
      offer.priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      offer.itemOffered.description += '. Cotización requerida.';
    }

    return offer;
  });

  // Find lowest price for priceRange
  const prices = tramites
    .filter(t => t.price_type === 'fijo' && t.fixed_price)
    .map(t => t.fixed_price);
  const minPrice = prices.length ? Math.min(...prices) : null;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `Trámites de ${category.name} en México`,
    "description": category.description || `Gestión profesional de trámites de ${category.name}`,
    "provider": {
      "@type": "Organization",
      "name": "MisTramites.mx",
      "url": "https://mistramites.mx"
    },
    "areaServed": {
      "@type": "Country",
      "name": "México"
    },
    "serviceType": category.name,
    ...(minPrice && { priceRange: `Desde $${minPrice} MXN` }),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `Servicios de ${category.name}`,
      "numberOfItems": tramites.length,
      "itemListElement": offers
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": "150",
      "reviewCount": "120"
    }
  };
};

/**
 * FAQ schema - generated dynamically from tramites.
 * Each tramite becomes FAQ questions about price, time, and documents.
 * 
 * Example: "¿Cuánto cuesta Alta de Placas en México?" 
 *          → "$1,200 MXN. Tiempo estimado: 4 días hábiles."
 */
export const buildCategoryFaqSchema = (category, tramites = []) => {
  if (!category || !tramites.length) return null;

  const questions = [];

  tramites.forEach(t => {
    // Question about price and time
    const priceText = t.price_type === 'fijo' && t.fixed_price
      ? `$${t.fixed_price.toLocaleString()} MXN`
      : 'Requiere cotización personalizada';
    const timeText = t.estimated_time_days
      ? `${t.estimated_time_days} días hábiles`
      : 'Variable según el trámite';

    questions.push({
      "@type": "Question",
      "name": `¿Cuánto cuesta ${t.name} en México?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": `El costo de ${t.name} es ${priceText}. Tiempo estimado de entrega: ${timeText}. ${t.description || ''}`
      }
    });

    // Question about required documents
    if (t.required_documents && t.required_documents.length > 0) {
      questions.push({
        "@type": "Question",
        "name": `¿Qué documentos necesito para ${t.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Para tramitar ${t.name} necesitas: ${t.required_documents.join(', ')}.`
        }
      });
    }
  });

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions
  };
};

/**
 * ItemList schema - for the categories listing page.
 * Shows all categories as a structured list in Google.
 */
export const buildCategoriesListSchema = (categories = []) => {
  if (!categories.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Categorías de Trámites en México",
    "description": "Todas las categorías de trámites gubernamentales disponibles en MisTramites.mx",
    "numberOfItems": categories.length,
    "itemListElement": categories.map((cat, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": cat.name,
      "description": cat.description,
      "url": `https://mistramites.mx/categoria/${cat.slug}`
    }))
  };
};

export default SchemaOrg;
